// src/modules/phone/RingCascadeManager.js
import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import DashboardLayout from '../../layouts/DashboardLayout';
import Modal from '../../components/shared/Modal';
import { useAuth } from '../auth/AuthContext';
import { getUsers } from '../users/userService';
import {
  getRingCascade, addToCascade, updateCascadeEntry, reorderCascade, removeFromCascade,
  getCascadeSettings, updateCascadeSettings, getRingCascadeEnabled, setRingCascadeEnabled
} from './phoneService';
import {
  FaPhoneAlt, FaPlus, FaTrash, FaSave, FaTimes, FaToggleOn, FaToggleOff,
  FaArrowUp, FaArrowDown, FaHeadset, FaMobileAlt, FaRobot, FaLock
} from 'react-icons/fa';

const Container = styled.div`
  padding: 20px;
  max-width: 900px;
`;

const PageTitle = styled.h1`
  color: ${props => props.theme.colors.textPrimary};
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Section = styled.div`
  background-color: ${props => props.theme.colors.surface};
  border-radius: 8px;
  padding: 24px;
  margin-bottom: 20px;
`;

const SectionTitle = styled.h2`
  color: ${props => props.theme.colors.primary};
  font-size: 1.1rem;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Label = styled.label`
  display: block;
  color: ${props => props.theme.colors.textSecondary};
  margin-bottom: 6px;
  font-size: 0.9rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const TextArea = styled.textarea`
  width: 100%;
  padding: 10px 12px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  min-height: 80px;
  resize: vertical;
  font-family: inherit;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
`;

const Select = styled.select`
  width: 100%;
  padding: 8px 10px;
  background-color: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface};
  border-radius: 6px;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.9rem;
  box-sizing: border-box;

  &:focus { outline: none; border-color: ${props => props.theme.colors.primary}; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const SaveButton = styled.button`
  background-color: ${props => props.theme.colors.primary};
  color: white;
  padding: 10px 24px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;

  &:hover { opacity: 0.9; }
  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const AddButton = styled(SaveButton)`
  padding: 10px 20px;
`;

const ToggleRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid ${props => props.theme.colors.background};

  &:last-child { border-bottom: none; }
`;

const ToggleLabel = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
`;

const ToggleDescription = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
  margin-top: 2px;
  line-height: 1.5;
`;

const ToggleButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  font-size: 1.8rem;
  color: ${props => props.isOn ? props.theme.colors.primary : props.theme.colors.textSecondary};
  display: flex;
  align-items: center;

  &:disabled { opacity: 0.5; cursor: not-allowed; }
`;

const Message = styled.div`
  padding: 10px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  background-color: ${props => props.error ? '#fa7f7f22' : '#4caf5022'};
  color: ${props => props.error ? '#fa7f7f' : '#4caf50'};
  font-size: 0.9rem;
`;

const HelpText = styled.p`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.82rem;
  margin-top: 6px;
  line-height: 1.5;
`;

const EntryCard = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 14px;
  margin-bottom: 10px;
  background-color: ${props => props.theme.colors.background};
  border-radius: 6px;
  opacity: ${props => props.dimmed ? 0.55 : 1};

  @media (max-width: 768px) {
    flex-wrap: wrap;
  }
`;

const OrderBadge = styled.div`
  min-width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.primary};
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: bold;
  flex-shrink: 0;
`;

const EntryInfo = styled.div`
  flex: 1;
  min-width: 140px;
`;

const EntryName = styled.div`
  color: ${props => props.theme.colors.textPrimary};
  font-size: 0.95rem;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const EntryMeta = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 0.8rem;
  margin-top: 3px;
`;

const Badge = styled.span`
  font-size: 0.72rem;
  padding: 2px 8px;
  border-radius: 10px;
  background-color: ${props => props.theme.colors.surface};
  color: ${props => props.theme.colors.textSecondary};
  display: inline-flex;
  align-items: center;
  gap: 5px;
`;

const Arrows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const IconButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.color || props.theme.colors.textSecondary};
  cursor: pointer;
  font-size: 0.95rem;
  padding: 4px 6px;

  &:hover:not(:disabled) { color: ${props => props.theme.colors.primary}; }
  &:disabled { opacity: 0.25; cursor: not-allowed; }
`;

const DurationSelect = styled(Select)`
  width: 110px;
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 32px 20px;
  color: ${props => props.theme.colors.textSecondary};
  line-height: 1.6;
`;

const AddForm = styled.div`
  margin-top: 16px;
  padding: 16px;
  background-color: ${props => props.theme.colors.background};
  border-radius: 6px;
`;

const FormRow = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 12px;

  @media (max-width: 768px) { grid-template-columns: 1fr; }
`;

const FormActions = styled.div`
  display: flex;
  gap: 10px;
  margin-top: 12px;
`;

const CancelButton = styled.button`
  background: none;
  border: 1px solid ${props => props.theme.colors.inputGray};
  color: ${props => props.theme.colors.textSecondary};
  padding: 10px 20px;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.95rem;

  &:hover { opacity: 0.85; }
`;

const CharCount = styled.div`
  text-align: right;
  font-size: 0.75rem;
  margin-top: 4px;
  color: ${props => props.over ? '#fa7f7f' : props.theme.colors.textSecondary};
`;

// Ring durations. The backend accepts any integer 5..120; these are the useful
// ones. ~6s per ring, matching the phrasing already used in the IVR menu editor.
const DURATIONS = [15, 18, 20, 24, 30, 36, 45, 60];
const MAX_MESSAGE_LEN = 500;

const emptyForm = { user_id: '', ring_duration: 20, forward_to: '' };

const RingCascadeManager = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';

  const [entries, setEntries] = useState([]);
  const [users, setUsers] = useState([]);
  const [enabled, setEnabled] = useState(false);
  const [settings, setSettings] = useState({
    ai_fallback_enabled: true, ai_fallback_message: '', no_answer_forward_to: null
  });

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState('');
  const [message, setMessage] = useState(null);

  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [deleteId, setDeleteId] = useState(null);

  const showMessage = (text, error = false) => {
    setMessage({ text, error });
    setTimeout(() => setMessage(null), 4000);
  };

  // Surface the server's own message — it explains 409 (already in cascade),
  // bad phone numbers, and out-of-range durations far better than we could.
  const errorText = (err, fallback) =>
    err?.response?.data?.message || err?.message || fallback;

  const loadAll = useCallback(async () => {
    try {
      setLoading(true);
      const [cascade, cfg, isOn, userList] = await Promise.all([
        getRingCascade(), getCascadeSettings(), getRingCascadeEnabled(), getUsers()
      ]);
      setEntries(cascade);
      setSettings(cfg);
      setEnabled(isOn);
      setUsers(Array.isArray(userList) ? userList : []);
    } catch (err) {
      showMessage(errorText(err, 'Error loading phone settings'), true);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (isAdmin) loadAll(); else setLoading(false); }, [isAdmin, loadAll]);

  // ─── Enable flag ─────────────────────────────────────────────────────────────
  const handleToggleEnabled = async () => {
    const next = !enabled;
    setEnabled(next);                                   // optimistic
    try {
      setSaving('enabled');
      await setRingCascadeEnabled(next);
      showMessage(next
        ? 'Ring cascade enabled — inbound calls now ring the list below.'
        : 'Ring cascade disabled — inbound calls use the previous behavior.');
    } catch (err) {
      setEnabled(!next);                                // revert
      showMessage(errorText(err, 'Error updating setting'), true);
    } finally {
      setSaving('');
    }
  };

  // ─── Reorder ─────────────────────────────────────────────────────────────────
  const move = async (index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= entries.length) return;

    const previous = entries;
    const next = [...entries];
    [next[index], next[target]] = [next[target], next[index]];
    const renumbered = next.map((e, i) => ({ ...e, ring_order: i }));
    setEntries(renumbered);                             // optimistic

    try {
      setSaving('reorder');
      const fresh = await reorderCascade(renumbered.map(e => ({ id: e.id, ring_order: e.ring_order })));
      setEntries(fresh);
    } catch (err) {
      setEntries(previous);                             // revert
      showMessage(errorText(err, 'Error reordering'), true);
    } finally {
      setSaving('');
    }
  };

  // ─── Per-entry edits ─────────────────────────────────────────────────────────
  const patchEntry = async (id, changes) => {
    const previous = entries;
    setEntries(entries.map(e => (e.id === id ? { ...e, ...changes } : e)));
    try {
      const updated = await updateCascadeEntry(id, changes);
      setEntries(list => list.map(e => (e.id === id ? updated : e)));
    } catch (err) {
      setEntries(previous);
      showMessage(errorText(err, 'Error updating entry'), true);
    }
  };

  // ─── Add ─────────────────────────────────────────────────────────────────────
  const availableUsers = users.filter(u => !entries.some(e => e.User?.id === u.id));

  const handleAdd = async () => {
    if (!form.user_id) return showMessage('Choose a user to add', true);
    try {
      setSaving('add');
      const payload = {
        user_id: Number(form.user_id),
        ring_duration: Number(form.ring_duration),
      };
      if (form.forward_to.trim()) payload.forward_to = form.forward_to.trim();

      const created = await addToCascade(payload);
      setEntries([...entries, created]);
      setForm(emptyForm);
      setShowForm(false);
      showMessage(`${created.User?.name || 'User'} added to the cascade`);
    } catch (err) {
      showMessage(errorText(err, 'Error adding user'), true);   // includes the 409 text
    } finally {
      setSaving('');
    }
  };

  // ─── Delete ──────────────────────────────────────────────────────────────────
  const handleConfirmDelete = async () => {
    try {
      await removeFromCascade(deleteId);                // 204, no body
      setEntries(entries.filter(e => e.id !== deleteId));
      showMessage('Removed from cascade');
    } catch (err) {
      showMessage(errorText(err, 'Error removing entry'), true);
    } finally {
      setDeleteId(null);
    }
  };

  // ─── Settings ────────────────────────────────────────────────────────────────
  const handleSaveSettings = async () => {
    try {
      setSaving('settings');
      const saved = await updateCascadeSettings({
        ai_fallback_enabled: settings.ai_fallback_enabled,
        ai_fallback_message: settings.ai_fallback_message || '',
        no_answer_forward_to: settings.no_answer_forward_to || null,
      });
      setSettings(saved);
      showMessage('Fallback settings saved');
    } catch (err) {
      showMessage(errorText(err, 'Error saving settings'), true);
    } finally {
      setSaving('');
    }
  };

  if (!isAdmin) {
    return (
      <DashboardLayout>
        <Container>
          <PageTitle><FaPhoneAlt /> Phone — Ring Cascade</PageTitle>
          <Section>
            <EmptyState>
              <FaLock size={28} style={{ marginBottom: 12, opacity: 0.6 }} />
              <div>Administrator access required.</div>
              <div style={{ fontSize: '0.85rem', marginTop: 6 }}>
                This page controls how inbound customer calls are routed.
              </div>
            </EmptyState>
          </Section>
        </Container>
      </DashboardLayout>
    );
  }

  if (loading) {
    return (
      <DashboardLayout>
        <Container><p style={{ color: '#ccc' }}>Loading...</p></Container>
      </DashboardLayout>
    );
  }

  const msgLen = (settings.ai_fallback_message || '').length;

  return (
    <DashboardLayout>
      <Container>
        <PageTitle><FaPhoneAlt /> Phone — Ring Cascade</PageTitle>
        {message && <Message error={message.error}>{message.text}</Message>}

        {/* ── Enable ─────────────────────────────────────────────────────────── */}
        <Section>
          <SectionTitle><FaToggleOn /> Inbound Call Routing</SectionTitle>
          <ToggleRow>
            <div>
              <ToggleLabel>Ring Cascade Enabled</ToggleLabel>
              <ToggleDescription>
                When ON, an inbound caller who reaches the softphone menu rings each person below in
                order, then falls through to the AI agent. When OFF, calls use the previous shared
                softphone behavior. Turning this off is an instant, safe rollback.
              </ToggleDescription>
            </div>
            <ToggleButton isOn={enabled} onClick={handleToggleEnabled} disabled={saving === 'enabled'}>
              {enabled ? <FaToggleOn /> : <FaToggleOff />}
            </ToggleButton>
          </ToggleRow>
        </Section>

        {/* ── Ring order ─────────────────────────────────────────────────────── */}
        <Section>
          <SectionTitle><FaHeadset /> Who Rings, In Order</SectionTitle>

          {entries.length === 0 ? (
            <EmptyState>
              <FaRobot size={28} style={{ marginBottom: 12, opacity: 0.6 }} />
              <div>No one is in the cascade.</div>
              <div style={{ fontSize: '0.85rem', marginTop: 6 }}>
                Inbound calls go straight to the AI agent.
              </div>
            </EmptyState>
          ) : (
            entries.map((entry, index) => (
              <EntryCard key={entry.id} dimmed={!entry.is_active}>
                <OrderBadge>{index + 1}</OrderBadge>

                <Arrows>
                  <IconButton
                    onClick={() => move(index, -1)}
                    disabled={index === 0 || saving === 'reorder'}
                    title="Ring earlier"
                  ><FaArrowUp /></IconButton>
                  <IconButton
                    onClick={() => move(index, 1)}
                    disabled={index === entries.length - 1 || saving === 'reorder'}
                    title="Ring later"
                  ><FaArrowDown /></IconButton>
                </Arrows>

                <EntryInfo>
                  <EntryName>
                    {entry.User?.name || `User ${entry.User?.id}`}
                    {entry.forward_to
                      ? <Badge><FaMobileAlt /> {entry.forward_to}</Badge>
                      : <Badge><FaHeadset /> Softphone</Badge>}
                  </EntryName>
                  <EntryMeta>
                    {entry.User?.phone || entry.User?.email}
                    {!entry.is_active && ' • inactive, skipped'}
                  </EntryMeta>
                </EntryInfo>

                <div>
                  <Label style={{ marginBottom: 3, fontSize: '0.72rem' }}>Rings for</Label>
                  <DurationSelect
                    value={entry.ring_duration}
                    onChange={e => patchEntry(entry.id, { ring_duration: Number(e.target.value) })}
                  >
                    {(DURATIONS.includes(entry.ring_duration)
                      ? DURATIONS
                      : [...DURATIONS, entry.ring_duration].sort((a, b) => a - b)
                    ).map(d => <option key={d} value={d}>{d}s</option>)}
                  </DurationSelect>
                </div>

                <ToggleButton
                  isOn={entry.is_active}
                  onClick={() => patchEntry(entry.id, { is_active: !entry.is_active })}
                  title={entry.is_active ? 'Skip this person' : 'Include this person'}
                  style={{ fontSize: '1.4rem' }}
                >
                  {entry.is_active ? <FaToggleOn /> : <FaToggleOff />}
                </ToggleButton>

                <IconButton color="#fa7f7f" onClick={() => setDeleteId(entry.id)} title="Remove">
                  <FaTrash />
                </IconButton>
              </EntryCard>
            ))
          )}

          {!showForm && (
            <>
              <AddButton
                onClick={() => { setForm(emptyForm); setShowForm(true); }}
                disabled={availableUsers.length === 0}
                style={{ marginTop: 16 }}
              >
                <FaPlus /> Add Person
              </AddButton>
              {availableUsers.length === 0 && (
                <HelpText>All users are already in the cascade.</HelpText>
              )}
            </>
          )}

          {showForm && (
            <AddForm>
              <FormRow>
                <div>
                  <Label>User</Label>
                  <Select value={form.user_id} onChange={e => setForm({ ...form, user_id: e.target.value })}>
                    <option value="">Select a user…</option>
                    {availableUsers.map(u => (
                      <option key={u.id} value={u.id}>{u.username} ({u.role})</option>
                    ))}
                  </Select>
                </div>
                <div>
                  <Label>Ring duration</Label>
                  <Select
                    value={form.ring_duration}
                    onChange={e => setForm({ ...form, ring_duration: Number(e.target.value) })}
                  >
                    {DURATIONS.map(d => <option key={d} value={d}>{d} seconds</option>)}
                  </Select>
                </div>
              </FormRow>

              <div>
                <Label>Forward to a phone number (optional)</Label>
                <Input
                  value={form.forward_to}
                  onChange={e => setForm({ ...form, forward_to: e.target.value })}
                  placeholder="+19789876543"
                />
                <HelpText>
                  Leave empty to ring this person's softphone (browser or mobile app). Enter a number
                  to ring their real phone over the carrier network instead.
                </HelpText>
              </div>

              <FormActions>
                <SaveButton onClick={handleAdd} disabled={saving === 'add'}>
                  <FaSave /> {saving === 'add' ? 'Adding…' : 'Add'}
                </SaveButton>
                <CancelButton onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                  <FaTimes /> Cancel
                </CancelButton>
              </FormActions>
            </AddForm>
          )}
        </Section>

        {/* ── Fallback ───────────────────────────────────────────────────────── */}
        <Section>
          <SectionTitle><FaRobot /> When Nobody Answers</SectionTitle>

          <div style={{ marginBottom: 16 }}>
            <Label>Forward to a phone number instead</Label>
            <Input
              value={settings.no_answer_forward_to || ''}
              onChange={e => setSettings({ ...settings, no_answer_forward_to: e.target.value })}
              placeholder="Leave empty to use the AI agent"
            />
            <HelpText>
              If set, this number rings after everyone above has been tried — and it
              <strong> takes priority over the AI agent</strong>.
            </HelpText>
          </div>

          <ToggleRow>
            <div>
              <ToggleLabel>Hand off to the AI agent</ToggleLabel>
              <ToggleDescription>
                Used only when no forwarding number is set above. If both are off, the caller hears a
                short apology and the call ends.
              </ToggleDescription>
            </div>
            <ToggleButton
              isOn={settings.ai_fallback_enabled}
              onClick={() => setSettings({ ...settings, ai_fallback_enabled: !settings.ai_fallback_enabled })}
            >
              {settings.ai_fallback_enabled ? <FaToggleOn /> : <FaToggleOff />}
            </ToggleButton>
          </ToggleRow>

          <div style={{ marginTop: 16 }}>
            <Label>Extra message before the AI answers (optional)</Label>
            <TextArea
              value={settings.ai_fallback_message || ''}
              onChange={e => setSettings({ ...settings, ai_fallback_message: e.target.value })}
              placeholder="Leave empty — recommended"
            />
            <CharCount over={msgLen > MAX_MESSAGE_LEN}>{msgLen} / {MAX_MESSAGE_LEN}</CharCount>
            <HelpText>
              Best left empty: the AI agent already greets the caller with “No one is available right
              now. Let me connect you to our AI assistant.” Anything typed here is spoken
              <em> in addition</em> to that.
            </HelpText>
          </div>

          <SaveButton
            onClick={handleSaveSettings}
            disabled={saving === 'settings' || msgLen > MAX_MESSAGE_LEN}
            style={{ marginTop: 16 }}
          >
            <FaSave /> {saving === 'settings' ? 'Saving…' : 'Save Fallback Settings'}
          </SaveButton>
        </Section>

        {deleteId !== null && (
          <Modal
            isOpen={deleteId !== null}
            title="Remove From Cascade"
            message="This person will no longer be rung on inbound calls. Continue?"
            onConfirm={handleConfirmDelete}
            onCancel={() => setDeleteId(null)}
          />
        )}
      </Container>
    </DashboardLayout>
  );
};

export default RingCascadeManager;
